import classNames from "classnames/bind";
import * as React from "react";
import { CSSProperties, KeyboardEvent } from "react";
import { Marker } from "~/sharedComponents/userflow/utils/marker";
import { withValidation } from "../../sharedHOC/withValidation/withValidation";
import { BlockForFullStory, blockForFullStory } from "../../utils/blockForFullStory";
import { validationThemes as ValidationThemes } from "../validation/ValidationLabel";
import { LabelText } from "./fieldsUI/LabelText";
import * as styles from "./Input.less";

const Component = React.Component;
const classNamesCtx = classNames.bind(styles);
type OnChangeAction<TValue = string | number> = (value: TValue) => any;

export const defaultNonInlineInputTheme: Pick<ThemedInputProps<inputTheme>, "theme"> = { theme: null };

export type InputIconProps = Omit<React.ComponentProps<typeof GenericInput>, "renderIcon">;

export type CommonInputProps<TValue = string | number> = Marker & {
    label?: string;
    value: TValue;
    onChangeAction?: OnChangeAction<TValue>;
    onBlurAction?: (event: React.FocusEvent<HTMLElement>) => void | any;
    runBlurAfterClick?: boolean;
    onClickAction?: any;
    onFocusAction?: any;
    onPasteAction?: (e: React.ClipboardEvent) => void;
    onKeyDown?: React.KeyboardEventHandler;
    onKeyUp?: React.KeyboardEventHandler;
    placeholder?: string;
    disabled?: boolean;
    autoComplete?: string;
    maxLength?: number;
    /** true for text selection, false for simple focus */
    autoFocus?: boolean;
    refCallback?: Function;
    showValidation?: boolean;
    /** shouldn't use this option and 'autoFocus' at the same time (to prevent double selection) */
    selectOnFocus?: boolean;
    tabIndex?: number;
    onDropAction?: (e: React.DragEvent<HTMLInputElement>) => boolean;
    readonly?: boolean;
    style?: CSSProperties;
    blockForFullStory?: boolean;
    onPaste?: (e: React.ClipboardEvent<HTMLInputElement>) => void;
    ["aria-label"]?: string;
    renderIcon?: (inputProps: InputIconProps) => React.ReactElement;
};

export interface ThemedInputProps<T, TValue = string | number> extends CommonInputProps<TValue> {
    theme?: T | T[];
}

export enum inputTheme {
    high = "high",
    align_center = "align_center",
    align_right = "align_right",
    weeklyView = "weeklyView",
    custom_field = "custom_field",
    /**
     * Used for lists in grid.
     * Paddings are the same as for list item.
     * Has no icon and focus state.
     */
    naked_list_search = "naked_list_search",
    error = "error",
    input_search = "input_search",
    header_search = "header_search",
    hidden = "hidden",
    datepicker_calendar = "datepicker_calendar",
    datepicker_calendar_view = "datepicker_calendar_view",
    datepicker_calendar_readonly = "datepicker_calendar_readonly",
    grid_editor = "grid_editor",
    inline = "inline",
    percent_size = "percent_size",
    table_editor = "table_editor",
    popup_edit_title = "popup_edit_title",
    naked = "naked",
    custom_filed_details = "custom_filed_details",
}

export interface InputProps<TValue = string | number> extends ThemedInputProps<inputTheme, TValue>, BlockForFullStory {
    type?: string;
    "aria-labelledby"?: string;
    "aria-invalid"?: boolean;
    "aria-errormessage"?: string;
}

export class GenericInput<
    TProps extends CommonInputProps,
    TBaseElement extends HTMLInputElement | HTMLTextAreaElement
> extends Component<TProps> {
    inputRef: React.RefObject<TBaseElement>;

    _isMouseDownOutsideTracked: boolean = false;
    _cleanupBlurTracker: F = null;
    _trackedBlurEvent: React.FocusEvent<HTMLElement> = null;

    constructor(props: TProps) {
        super(props);
        this.inputRef = React.createRef();

        this.onChange = this.onChange.bind(this);
        this.onPaste = this.onPaste.bind(this);
        this.onBlur = this.onBlur.bind(this);
        this.onFocus = this.onFocus.bind(this);
        this.onClick = this.onClick.bind(this);
        this.onKeyDown = this.onKeyDown.bind(this);
    }

    componentDidMount() {
        if (this.props.refCallback) {
            this.props.refCallback(this.inputRef.current);
        }

        if (this.props.autoFocus !== undefined) {
            this.props.autoFocus ? this.focusInput(true) : this.focusInput(false);
        }
    }

    startTrackingBlurOnMouseUp() {
        if (!this.props.runBlurAfterClick || this._cleanupBlurTracker) {
            return;
        }

        this._isMouseDownOutsideTracked = false;
        this._trackedBlurEvent = null;

        const isOutside = (e: MouseEvent) =>
            !(this.inputRef.current && e.target && this.inputRef.current.contains(e.target as Node));
        const onMouseDown = (e: MouseEvent) => {
            if (isOutside(e)) {
                this._isMouseDownOutsideTracked = true;
            }
        };

        const onMouseUp = (e: MouseEvent) => {
            if (isOutside(e) && this._trackedBlurEvent) {
                this._isMouseDownOutsideTracked = false;
                this.onBlur(this._trackedBlurEvent);
            }
        };
        document.addEventListener("mousedown", onMouseDown, false);
        document.addEventListener("mouseup", onMouseUp, false);

        this._cleanupBlurTracker = () => {
            document.removeEventListener("mousedown", onMouseDown, false);
            document.removeEventListener("mouseup", onMouseUp, false);

            this._isMouseDownOutsideTracked = false;
            this._trackedBlurEvent = null;
            this._cleanupBlurTracker = null;
        };
    }

    onChange(e: React.ChangeEvent<TBaseElement>) {
        if (this.props.onChangeAction) {
            this.props.onChangeAction(e.target.value);
        }
    }

    onPaste(e: React.ClipboardEvent) {
        if (this.props.onPasteAction) {
            this.props.onPasteAction(e);
        }
    }

    onBlur(event: React.FocusEvent<HTMLElement>) {
        if (this._isMouseDownOutsideTracked) {
            event.persist();
            this._trackedBlurEvent = event;
            return;
        }

        this._cleanupBlurTracker?.();
        return this.props.onBlurAction && this.props.onBlurAction(event);
    }

    select() {
        if (document.activeElement === this.inputRef.current) {
            this.inputRef.current.select();
        }
    }

    onFocus() {
        const { selectOnFocus, autoFocus } = this.props;

        if (selectOnFocus) {
            if (autoFocus) {
                this.select();
            } else {
                this._setTimeout(() => this.select(), 0);
            }
        }

        this.startTrackingBlurOnMouseUp();
        return this.props.onFocusAction && this.props.onFocusAction();
    }

    _setTimeout(callback: F, timeout: number) {
        if (process.env.NODE_ENV === "test") {
            // userEvents.type("abc") doesn't work in tests when input.select() is called in timeout
            // so run this code synchronously in tests.
            // See usage in onFocus callback above
            callback();
        } else {
            return setTimeout(callback, timeout);
        }
    }

    onClick() {
        return this.props.onClickAction && this.props.onClickAction();
    }

    onKeyDown(event: KeyboardEvent<HTMLInputElement>) {
        return this.props.onKeyDown && this.props.onKeyDown(event);
    }

    focusInput = (selectContent: boolean, options?: FocusOptions) => {
        const input = this.inputRef.current;
        selectContent ? input.select() : input.focus(options);
    };
    isInputFocused = () => {
        return document.activeElement === this.inputRef.current;
    };

    componentWillUnmount() {
        this._cleanupBlurTracker?.();
    }

    renderIcon() {
        const { renderIcon, ...props } = this.props;

        if (renderIcon) {
            return renderIcon(props);
        }

        return null;
    }

    renderWithLabel(input: JSX.Element) {
        const { label } = this.props;

        if (!label) {
            return input;
        }

        return (
            <label>
                <LabelText>{label}</LabelText>
                {input}
            </label>
        );
    }
}

export class Input extends GenericInput<InputProps, HTMLInputElement> {
    getClassName(theme: string) {
        switch (theme) {
            case inputTheme.high:
                return styles.high;
            case inputTheme.align_center:
                return styles.align_center;
            case inputTheme.align_right:
                return styles.align_right;
            case inputTheme.custom_field:
                return styles.custom_field;
            case inputTheme.input_search:
                return styles.input_search;
            case inputTheme.header_search:
                return styles.header_search;
            case inputTheme.datepicker_calendar:
                return styles.datepicker_calendar;
            case inputTheme.datepicker_calendar_view:
                return styles.datepicker_calendar_view;
            case inputTheme.datepicker_calendar_readonly:
                return styles.datepicker_calendar_readonly;
            case inputTheme.hidden:
                return styles.hidden;
            case inputTheme.grid_editor:
                return styles.grid_editor;
            case inputTheme.inline:
                return styles.inline;
            case inputTheme.percent_size:
                return styles.percent_size;
            case inputTheme.table_editor:
                return styles.table_editor;
            case inputTheme.error:
                return styles.error;
            case inputTheme.popup_edit_title:
                return styles.popup_edit_title;
            case inputTheme.naked:
                return styles.naked;
            case inputTheme.naked_list_search:
                return styles.naked_list_search;
            case inputTheme.custom_filed_details:
                return styles.custom_filed_details;
        }
    }

    unselect() {
        this.inputRef.current.setSelectionRange(0, 0);
    }

    render() {
        const inputClasses = [styles.input];
        const {
            value,
            disabled,
            type,
            placeholder = "",
            autoComplete = "off",
            maxLength = null,
            theme = null,
            showValidation,
            onPaste,
            "data-testid": dataTestId,
        } = this.props;

        if (theme instanceof Array) {
            theme.forEach(item => {
                inputClasses.push(this.getClassName(item));
            });
        } else {
            inputClasses.push(this.getClassName(theme));
        }
        if (showValidation) {
            inputClasses.push(styles.error);
        }

        const input = (
            <>
                <input
                    data-testid={dataTestId}
                    className={blockForFullStory(classNamesCtx(...inputClasses), this.props)}
                    readOnly={this.props.readonly}
                    disabled={disabled || false}
                    type={type || "text"}
                    value={value || ""}
                    onChange={this.onChange}
                    placeholder={placeholder}
                    onBlur={this.onBlur}
                    onClick={this.onClick}
                    onFocus={this.onFocus}
                    autoComplete={autoComplete}
                    maxLength={maxLength}
                    ref={this.inputRef}
                    onKeyDown={this.onKeyDown}
                    onKeyUp={this.props.onKeyUp}
                    tabIndex={this.props.tabIndex}
                    onDrop={this.props.onDropAction}
                    spellCheck={false}
                    style={this.props.style}
                    onPaste={onPaste}
                    aria-label={this.props["aria-label"]}
                    aria-labelledby={this.props["aria-labelledby"]}
                    aria-invalid={this.props["aria-invalid"]}
                    aria-errormessage={this.props["aria-errormessage"]}
                />
                {this.renderIcon()}
            </>
        );

        return this.renderWithLabel(input);
    }
}

const WithValidation = withValidation(Input);
export const InputWithValidation = ({
    label,
    validationThemes,
    ...props
}: React.ComponentProps<typeof WithValidation>) => (
    <WithValidation
        label={label}
        validationThemes={label && !validationThemes ? ValidationThemes.input_with_label : validationThemes}
        {...props}
    />
);
